import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { FileText, FileSpreadsheet, Clock, ShieldCheck, Crown, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { ADMIN_SECTIONS } from "../lib/adminSections";
import UserAvatar from "./UserAvatar";

const fmtDur = (sec) => {
  if (!sec) return "0 min";
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  return `${Math.floor(sec / 3600)} h ${String(Math.round((sec % 3600) / 60)).padStart(2, "0")}`;
};

const isoDay = (d) => d.toISOString().slice(0, 10);

// Choix de la période avant export : le rapport affiche exactement la période demandée.
function PeriodModal({ person, fmt, onClose, onConfirm }) {
  const [choice, setChoice] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const today = new Date();

  const periods = [
    { key: "all", label: "Toute la période" },
    { key: "7d", label: "7 derniers jours" },
    { key: "30d", label: "30 derniers jours" },
    { key: "month", label: "Mois en cours" },
    { key: "year", label: "Année en cours" },
    { key: "custom", label: "Période personnalisée" },
  ];

  const range = () => {
    const t = new Date(today);
    if (choice === "7d") { const f = new Date(t); f.setDate(f.getDate() - 6); return { from: isoDay(f), to: isoDay(t) }; }
    if (choice === "30d") { const f = new Date(t); f.setDate(f.getDate() - 29); return { from: isoDay(f), to: isoDay(t) }; }
    if (choice === "month") return { from: `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-01`, to: isoDay(t) };
    if (choice === "year") return { from: `${t.getFullYear()}-01-01`, to: isoDay(t) };
    if (choice === "custom") return custom;
    return {};
  };

  const confirm = () => {
    const r = range();
    if (choice === "custom" && (!r.from || !r.to)) return toast.error("Choisissez la date de début et de fin.");
    if (r.from && r.to && r.from > r.to) return toast.error("La date de début doit précéder la date de fin.");
    onConfirm(r);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="period-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="mt-16 w-full max-w-md rounded-2xl border border-white/10 bg-elevated p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-700 text-white">
            <CalendarDays size={17} className="text-cyan" /> Période du rapport {fmt === "xlsx" ? "Excel" : "PDF"}
          </h3>
          <button onClick={onClose} data-testid="period-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>
        <p className="mb-4 text-xs text-ink-secondary">
          Rapport de <span className="text-white">{person.first_name} {person.last_name}</span> — choisissez la période à inclure :
        </p>
        <div className="grid gap-2 sm:grid-cols-2" data-testid="period-choices">
          {periods.map((p) => (
            <button key={p.key} onClick={() => setChoice(p.key)} data-testid={`period-${p.key}`}
              className={`rounded-xl border px-4 py-3 text-left text-sm transition-all ${choice === p.key ? "border-cyan bg-cyan/10 text-cyan" : "border-white/10 bg-surface text-ink-secondary hover:border-white/30"}`}>
              {p.label}
            </button>
          ))}
        </div>
        {choice === "custom" && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-tertiary">Du</span>
              <input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} data-testid="period-from"
                className="w-full rounded-xl border border-white/10 bg-surface px-3 py-2.5 text-sm text-white outline-none focus:border-cyan [color-scheme:dark]" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-tertiary">Au</span>
              <input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} data-testid="period-to"
                className="w-full rounded-xl border border-white/10 bg-surface px-3 py-2.5 text-sm text-white outline-none focus:border-cyan [color-scheme:dark]" />
            </label>
          </div>
        )}
        <div className="mt-5 flex gap-3">
          <button onClick={confirm} data-testid="period-confirm" className="aca-btn-primary flex-1">
            Générer le rapport
          </button>
          <button onClick={onClose} data-testid="period-cancel" className="aca-btn-secondary flex-1">Annuler</button>
        </div>
      </motion.div>
    </div>
  );
}

// Personnel administrateur (super admin uniquement) : tous les admins ayant des droits
// sur des sections, leur activité (temps de connexion) et l'export PDF / XLS du rapport complet.
// Clic sur une ligne = ouverture de sa fiche.
export function AdminPersonnel({ onOpen }) {
  const [rows, setRows] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [exportFor, setExportFor] = useState(null); // { person, fmt }

  const load = useCallback(() => {
    api.get("/admin/personnel").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (p, fmt, period) => {
    setDownloading(`${p.id}-${fmt}`);
    try {
      const params = new URLSearchParams();
      if (period?.from) params.set("from", period.from);
      if (period?.to) params.set("to", period.to);
      const qs = params.toString();
      const { data } = await api.get(`/admin/personnel/${p.id}/activity-${fmt}${qs ? `?${qs}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activite_${p.first_name}_${p.last_name}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Rapport ${fmt === "xlsx" ? "Excel" : "PDF"} de ${p.first_name} ${p.last_name} téléchargé.`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setDownloading(""); setExportFor(null); }
  };

  return (
    <div className="border border-white/10 bg-surface p-5 sm:p-6" data-testid="personnel-panel">
      <h2 className="flex items-center gap-2 font-display text-xl font-700 text-white">
        <ShieldCheck size={18} className="text-amber-400" /> Personnel administrateur
      </h2>
      <p className="mt-1 max-w-2xl text-sm text-ink-secondary">
        Tous les administrateurs ayant des droits sur des sections, avec leur temps de connexion.
        Cliquez sur une ligne pour ouvrir sa fiche. Exportez le rapport complet d'activité
        (par heure, jour, mois, année — période au choix) en PDF ou en Excel.
      </p>

      <div className="mt-5 space-y-3">
        {(rows || []).map((p) => (
          <div key={p.id} onClick={() => onOpen?.(p)}
            className="flex cursor-pointer flex-wrap items-center gap-4 rounded-xl border border-white/10 bg-elevated p-4 transition-colors hover:border-cyan/40"
            data-testid={`personnel-row-${p.id}`} title="Cliquez pour ouvrir la fiche">
            <UserAvatar user={p} size={44} testid={`personnel-avatar-${p.id}`} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm font-600 text-white">
                {p.first_name} {p.last_name}
                {p.is_super
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-600 uppercase text-amber-300"><Crown size={10} /> Super admin</span>
                  : <span className="rounded-full bg-cyan/15 px-2 py-0.5 text-[10px] font-600 uppercase text-cyan">Sous-admin</span>}
              </div>
              <a href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()} className="block truncate text-xs text-ink-tertiary underline-offset-2 hover:text-cyan hover:underline">{p.email}</a>
              <div className="mt-1.5 flex max-w-lg flex-wrap gap-1">
                {(p.permissions || []).map((perm) => (
                  <span key={perm} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-600 uppercase tracking-wide text-ink-secondary" data-testid={`personnel-perm-${p.id}-${perm}`}>
                    {ADMIN_SECTIONS.find((x) => x.perm === perm)?.label || perm}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right text-xs text-ink-secondary">
              <div className="flex items-center justify-end gap-1.5 text-white"><Clock size={12} className="text-cyan" /> <span className="font-700 tabular-nums" data-testid={`personnel-time-${p.id}`}>{fmtDur(p.total_sec)}</span></div>
              <div className="mt-0.5">{p.sessions} session(s) · {p.pages} page(s)</div>
              <div className="mt-0.5 text-ink-tertiary">Dernière activité : {p.last_seen ? new Date(p.last_seen).toLocaleString("fr-FR") : "—"}</div>
            </div>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setExportFor({ person: p, fmt: "pdf" })} disabled={!!downloading} data-testid={`personnel-pdf-${p.id}`}
                className="aca-btn-secondary">
                <FileText size={13} /> {downloading === `${p.id}-pdf` ? "Export…" : "PDF"}
              </button>
              <button onClick={() => setExportFor({ person: p, fmt: "xlsx" })} disabled={!!downloading} data-testid={`personnel-xls-${p.id}`}
                className="aca-btn-secondary">
                <FileSpreadsheet size={13} /> {downloading === `${p.id}-xlsx` ? "Export…" : "XLS"}
              </button>
            </div>
          </div>
        ))}
        {rows && rows.length === 0 && (
          <p className="rounded-xl border border-dashed border-white/15 px-4 py-5 text-center text-sm text-ink-tertiary">Aucun administrateur trouvé.</p>
        )}
        {!rows && <p className="py-5 text-center text-sm text-ink-tertiary">Chargement…</p>}
      </div>

      {exportFor && (
        <PeriodModal person={exportFor.person} fmt={exportFor.fmt}
          onClose={() => setExportFor(null)}
          onConfirm={(period) => download(exportFor.person, exportFor.fmt, period)} />
      )}
    </div>
  );
}
